import React, { useEffect, useRef } from "react";
import Grain from "../Grain/Grain";
import Reveal from "../Reveal/Reveal";
import "./styles.css";

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const Icon = ({ children }) => (
  <div className="ico">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      {children}
    </svg>
  </div>
);

const Arrow = () => (
  <span className="arrow">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 12h14m-6-6 6 6-6 6" />
    </svg>
  </span>
);

const STEPS = [
  {
    pill: "Pedido cirúrgico",
    title: "Você descreve. O LetsDoc redige.",
    text: "Digite ou dite um breve histórico e cole o laudo. O pedido sai estruturado com CID, procedimento e justificativa.",
    checks: ["CID e procedimento preenchidos", "Justificativa clínica redigida", "Pronto para revisão do médico"],
    from: <path d="M12 3v8m0 0a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v3a3 3 0 0 0 3 3zM6 11a6 6 0 0 0 12 0M12 17v4" />,
    to: (
      <>
        <rect x="5" y="3" width="14" height="18" rx="2" />
        <path d="M9 8h6M9 12h6M9 16h4" />
      </>
    ),
  },
  {
    pill: "Negativas e recursos",
    title: "Menos glosa. Recurso técnico em minutos.",
    text: "Pedidos e pacotes de OPME fundamentados desde a origem reduzem negativas das operadoras. Quando a glosa vem, o LetsDoc gera o recurso técnico otimizado e bem embasado.",
    checks: ["Menos devoluções por negativa", "Recurso técnico bem fundamentado", "Resposta à operadora mais rápida"],
    from: (
      <>
        <rect x="5" y="3" width="14" height="18" rx="2" />
        <path d="M9 8h6M9 12h6" />
      </>
    ),
    to: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
  },
  {
    pill: "Voz e OCR",
    title: "Você fala. O LetsDoc entende.",
    text: "Transcrição de voz durante a consulta e leitor OCR para digitalizar documentos e códigos de material.",
    checks: ["Voz para texto na consulta", "Leitura OCR de códigos", "Nada digitado duas vezes"],
    from: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M7 9h10M7 13h6" />
      </>
    ),
    to: (
      <path d="M12 3a4 4 0 0 0-4 4v1a3 3 0 0 0-3 3v1a3 3 0 0 0 3 3v1a4 4 0 0 0 8 0v-1a3 3 0 0 0 3-3v-1a3 3 0 0 0-3-3V7a4 4 0 0 0-4-4z" />
    ),
  },
];

// Barra lateral que enche conforme o usuário rola pelas etapas.
const useTimelineProgress = () => {
  const listRef = useRef(null);
  const barRef = useRef(null);

  useEffect(() => {
    const update = () => {
      const rect = listRef.current.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, (window.innerHeight * 0.55 - rect.top) / rect.height));
      barRef.current.style.height = `${progress * 100}%`;
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);

  return { listRef, barRef };
};

const Agents = () => {
  const { listRef, barRef } = useTimelineProgress();

  return (
    <section className="agents dossie" id="agents">
      <div className="margin-line" />
      <Grain variant="paper" />
      <div className="container">
        <Reveal className="section-head">
          <span className="tag">Fluxo de trabalho</span>
          <h2 className="display">Três etapas. Nenhum retrabalho.</h2>
          <p>
            Você insere o histórico e o laudo. A plataforma monta o pedido, o pacote de cirurgia e o
            OPME prontos para enviar.
          </p>
        </Reveal>

        <div className="wrap">
          <div className="timeline">
            <div className="line">
              <div className="progress" ref={barRef} />
            </div>
          </div>
          <div className="agent-list" ref={listRef}>
            {STEPS.map((step, i) => (
              <Reveal as="article" className="ficha" key={step.pill}>
                <span className="tabhead">
                  Etapa {String(i + 1).padStart(2, "0")}/{String(STEPS.length).padStart(2, "0")}
                </span>
                <div className="inner">
                  <div className="side">
                    <span className="pill-ink">{step.pill}</span>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                    <div className="checks">
                      {step.checks.map((check) => (
                        <div className="check" key={check}>
                          <span className="box">
                            <CheckIcon />
                          </span>
                          {check}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="side visual" aria-hidden="true">
                    <Icon>{step.from}</Icon>
                    <Arrow />
                    <Icon>{step.to}</Icon>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Agents;
